import { Spacer, Text } from "@zapier/design-system";
import MediaSlideshow, {
  MediaItem,
} from "src/components/MediaSlideshow/MediaSlideshow";
import { TemplateCTAs } from "src/components/TemplateCTAs/TemplateCTAs";
import { ZapBreakdown } from "src/components/ZapBreakdown/ZapBreakdown";
import { ZapBreakdownSkeleton } from "src/components/ZapBreakdown/ZapBreakdownSkeleton";
import { Template, ZapBreakdownDetails } from "src/types";
import styles from "./TemplateHeroSection.module.css";
import { tryTemplateActions } from "src/components/TryTemplateButton";
import { TemplateBookmarkActions } from "src/components/TemplateBookmarkButton";

type TemplateHeroSectionProps = {
  template: Template;
  zapBreakdownDetails: ZapBreakdownDetails | null;
  isLoggedIn?: boolean;
  isDraftModeEnabled?: boolean;
  isModal?: boolean;
  tryTemplateActions: tryTemplateActions;
  templateBookmarkActions: TemplateBookmarkActions;
  hasBookmarking: boolean;
  hasTemplatePage: boolean;
  apiHostName?: string;
};

/**
 * TemplateHeroSection is a component that displays the hero section of a template.
 * It includes the template name, description, and a list of CTAs.
 */
export const TemplateHeroSection = ({
  template,
  isLoggedIn = false,
  isDraftModeEnabled = false,
  zapBreakdownDetails,
  isModal = false,
  tryTemplateActions,
  templateBookmarkActions,
  hasBookmarking,
  hasTemplatePage,
  apiHostName,
}: TemplateHeroSectionProps) => {
  const mediaItems: MediaItem[] = template.previewImage?.url
    ? [
        {
          type: "image",
          src: `${apiHostName || ""}${template.previewImage.url}`,
          alt: template.previewImage.alt,
        },
        ...(template.additionalMedia
          ?.filter(
            (media): media is typeof media & { url: string } => !!media.url,
          )
          .map(
            (media): MediaItem => ({
              type: media.mimeType?.includes("image") ? "image" : "video",
              src: media.url,
              alt: media.alt,
            }),
          ) ?? []),
      ]
    : [];

  return (
    <div className={`${styles.heroContent} ${isModal ? styles.modal : ""}`}>
      <div className={styles.heroContentLeft}>
        <Text
          tag="h1"
          type={isModal ? "SectionHeader" : "PageHeader"}
          className={isModal ? styles.modalTitle : styles.title}
        >
          {template.nameMechanical || template.name}{" "}
          {isDraftModeEnabled && "(Draft)"}
        </Text>
        <Spacer height={16} />
        <Text tag="p" type="Body1Multiline" color="TextDefault">
          {template.subtitle}
        </Text>
        {template.subtitle && <Spacer height={20} />}
        <TemplateCTAs
          template={template}
          isLoggedIn={isLoggedIn}
          templateBookmarkActions={templateBookmarkActions}
          hasBookmarking={hasBookmarking}
          tryTemplateActions={tryTemplateActions}
          hasTemplatePage={hasTemplatePage}
          apiHostName={apiHostName}
        />
      </div>
      <div
        className={
          isModal
            ? `${styles.modalHeroContentRight} ${styles.heroContentRight}`
            : `${styles.pageHeroContentRight} ${styles.heroContentRight}`
        }
      >
        {mediaItems.length > 0 ? (
          <MediaSlideshow mediaItems={mediaItems} />
        ) : zapBreakdownDetails ? (
          <ZapBreakdown
            title={zapBreakdownDetails.title}
            firstStep={zapBreakdownDetails.firstStep}
            lastStep={zapBreakdownDetails.lastStep}
            totalStepCount={zapBreakdownDetails.totalStepCount}
            services={zapBreakdownDetails.services}
            servicesList={zapBreakdownDetails.servicesList}
          />
        ) : (
          <ZapBreakdownSkeleton />
        )}
      </div>
    </div>
  );
};
